import { formatTaskTimeRange } from '@/tasks/time-grid';
import type { Task } from '@/tasks/task';

/**
 * TaskTimeField の props。
 */
export interface TaskTimeFieldProps {
  /** 時刻を出すタスク */
  task: Task;
  /** 直せるか。直せないときは文字で出す */
  editable: boolean;
  /** 入力欄の見た目（詳細のほかの欄と揃える） */
  fieldClass: string;
  /** 時刻を直したときに呼ばれる */
  onChange: (patch: Pick<Task, 'startTime' | 'endTime'>) => void;
}

/**
 * 時刻の刻み（秒）。5 分刻みで選べるようにする。
 *
 * 刻みの起点は min で 00:00 に固定する。min が無いとブラウザは今の値を起点にするため、
 * カレンダーから取り込んだ 13:58 のような半端な時刻では 13・28・43・58 分しか選べなくなる
 */
/** @graph-connects none */
const TIME_STEP_SECONDS = 300;

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business タスクの時刻を常に出す。時刻の無いタスクは終日とし、直せるときは開始・終了を 5 分刻みで入れさせる。開始を消すと終日に戻す
 * @graph-connects formatTaskTimeRange [calls] 読むだけのときの時刻の表記を作る
 */
export function TaskTimeField({ task, editable, fieldClass, onChange }: TaskTimeFieldProps) {
  if (!editable) {
    return (
      <span className="tabular-nums text-[var(--color-text-primary)]">
        {formatTaskTimeRange(task) === '' ? '終日' : formatTaskTimeRange(task)}
      </span>
    );
  }
  return (
    <span className="flex items-center gap-2">
      <input
        type="time"
        step={TIME_STEP_SECONDS}
        min="00:00"
        aria-label="開始時刻"
        value={task.startTime ?? ''}
        onChange={(event) => {
          // 開始を消したら終日に戻す（終了だけ残しても時間軸に置けない）
          const startTime = event.target.value === '' ? undefined : event.target.value;
          onChange({ startTime, endTime: startTime === undefined ? undefined : task.endTime });
        }}
        className={`${fieldClass} w-32 tabular-nums`}
      />
      <span className="text-[var(--color-text-muted)]">〜</span>
      <input
        type="time"
        step={TIME_STEP_SECONDS}
        min="00:00"
        aria-label="終了時刻"
        disabled={task.startTime === undefined}
        value={task.endTime ?? ''}
        onChange={(event) => {
          onChange({
            startTime: task.startTime,
            endTime: event.target.value === '' ? undefined : event.target.value,
          });
        }}
        className={`${fieldClass} w-32 tabular-nums disabled:opacity-50`}
      />
      {task.startTime === undefined ? (
        <span className="text-xs text-[var(--color-text-muted)]">終日</span>
      ) : null}
    </span>
  );
}
